import { useFormikContext } from 'formik';
import { useContext, useMemo } from 'react';

import './CustomerLeadStepPurchaseItemsTab.scss';

import { TagListSelectField } from 'src/components/base/fields/TagListSelectField';
import { YesNoSelectField } from 'src/components/base/fields/YesNoSelectField';
import { ReferenceContext } from 'src/components/contexts/ReferenceContext/ReferenceContext.context';
import type { ICustomerLeadProductPurchaseProductTypesTabFormValues } from 'src/components/customers/CustomerLeadTypes';
import { useDebounceEffect } from 'src/hooks/useDebounceEffect';
import { CustomersContext } from 'src/pages/CustomersPage/CustomersPage.context';
import type { ISelectOption } from 'src/types';

type CustomerLeadStepPurchaseItemsTabProps = {
  onChange: (newValues: ICustomerLeadProductPurchaseProductTypesTabFormValues) => void;
};

export function CustomerLeadStepPurchaseItemsTab({ onChange }: Readonly<CustomerLeadStepPurchaseItemsTabProps>) {
  const { values, validateForm } = useFormikContext<ICustomerLeadProductPurchaseProductTypesTabFormValues>();

  const { productTypes } = useContext(ReferenceContext);
  const { customerLeadProduct } = useContext(CustomersContext);

  const productTypesOptions = useMemo(
    () =>
      productTypes
        .filter((productType) => productType.id !== customerLeadProduct?.productTypeId)
        .map(
          (productType) =>
            ({
              label: productType.name,
              value: productType.id,
            }) as ISelectOption
        ),
    [productTypes, customerLeadProduct?.productTypeId]
  );

  useDebounceEffect(
    () => {
      void (async () => {
        const errors = await validateForm(values);

        if (Object.keys(errors).length === 0) {
          onChange(values);
        }
      })();
    },
    [values],
    1000
  );

  return (
    <div className="customer-lead-step-purchase-items-tab__container">
      <div className="customer-lead-step-purchase-items-tab__container__row">
        <TagListSelectField
          name="purchaseProductTypes"
          label="Other Product Types"
          options={productTypesOptions}
          description="Does your company purchase any of the following items?"
          multiselect={true}
        />
      </div>
      <div className="customer-lead-step-purchase-items-tab__container__row">
        <YesNoSelectField
          name="customerPickup"
          label="Customer Pickup"
          description="Does your company have the capability to use its own trucks for product pickup and delivery?"
        />
      </div>
    </div>
  );
}
